import { Link } from 'react-router-dom';
import { ArrowUpRight, Bell, CalendarCheck, Check, FileText, Globe, Users } from '@phosphor-icons/react';
import { PageHero, Reveal, Chapter } from '../lib/motion';
import { VoltButton } from '../components/site/FormBits';
import { useSEO } from '../lib/seo';

const covers = [
  'Calibrated instruments and certificates',
  'Machinery and compressor maintenance',
  'Air-conditioning and building services',
  'Pest control, IT support and service contracts',
  'Contractor contacts and visit history',
  'Reports, notes and next due dates',
];

const steps = [
  ['Register free', 'Sign in with Google and tell us the company and records you want to track.'],
  ['Founder approval', 'Vox reviews each pilot registration personally and enables your workspace.'],
  ['Track and renew', 'Add service items, upload reports, receive reminders and request another visit when needed.'],
];

const CoverItem = ({ text }) => (
  <div className="flex items-start gap-3 border-b border-line py-3 last:border-b-0">
    <Check size={16} weight="bold" className="mt-0.5 shrink-0 text-volt" />
    <span className="text-sm leading-relaxed text-steel">{text}</span>
  </div>
);

const ServiceManagerLanding = () => {
  useSEO({
    title: 'Vox Service Manager — Service Records & Renewal Reminders',
    description: 'Free pilot workspace for companies to track service records, contractor details, certificates and renewal reminders anywhere in the world.',
    path: '/service-manager',
  });

  return (
    <div data-testid="service-manager-page">
      <PageHero
        eyebrow="Vox Service Manager"
        lines={[<>Service records.</>, <span key="l2">Renewals. <span className="text-volt">Handled.</span></span>]}
        sub="A free pilot workspace for companies to track service records, contractor details, certificates and renewal reminders — anywhere in the world."
      />

      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <p className="mono-label text-volt">What it replaces</p>
              <h2 className="mt-4 font-display text-3xl font-black tracking-tight text-parchment sm:text-4xl">The spreadsheet, WhatsApp thread and forgotten certificate.</h2>
              <p className="mt-5 text-base leading-relaxed text-steel">
                Vox Service Manager gives one company account a clean place to see what needs attention,
                who handles it, what happened during the last visit, and when the next renewal is due.
              </p>
              <div className="mt-8">
                <Link to="/account?next=/dashboard/services" data-testid="service-manager-start-button">
                  <VoltButton>Get started free <ArrowUpRight size={16} weight="bold" /></VoltButton>
                </Link>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-steel">Free during pilot · Founder approved · No card required</p>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="border border-line bg-ink p-8" data-testid="service-manager-coverage-card">
                <p className="mono-label text-volt">What it covers</p>
                <div className="mt-6">
                  {covers.map((item) => <CoverItem key={item} text={item} />)}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-ink bg-grid">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <Chapter no="01" label="How it works" title="Register. Approve. Track." />
          <div className="mt-10 grid gap-4 md:grid-cols-3" data-testid="service-manager-steps">
            {steps.map(([title, text], i) => (
              <Reveal key={title} delay={i * 0.06}>
                <div className="h-full border border-line bg-surface p-7">
                  <p className="font-mono text-xs font-bold tracking-[0.2em] text-volt">{String(i + 1).padStart(2, '0')}</p>
                  <h3 className="mt-4 font-display text-xl font-black text-parchment">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-steel">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <Chapter no="02" label="Included in the pilot" title="Simple records. Useful reminders." />
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              [FileText, 'Service records', 'Item, site, contractor, notes, last visit and next due date.'],
              [Bell, 'Renewal reminders', 'Automatic emails 30, 14 and 7 days before due, plus on the due date.'],
              [CalendarCheck, 'Visit history', 'Record what happened, attach the report and set the next date.'],
              [Users, 'Marketplace link', 'Request another visit and turn a due item into an RFQ when needed.'],
            ].map(([Icon, title, text], i) => (
              <Reveal key={title} delay={i * 0.05}>
                <div className="h-full border border-line bg-ink p-7 transition-colors duration-300 hover:border-volt/60">
                  <Icon size={22} weight="bold" className="text-volt" />
                  <h3 className="mt-4 font-display text-lg font-black text-parchment">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-steel">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-ink">
        <div className="mx-auto max-w-4xl px-6 py-20 text-center lg:px-10 lg:py-28">
          <Globe size={28} weight="bold" className="mx-auto text-volt" />
          <h2 className="mt-5 font-display text-3xl font-black tracking-tight text-parchment sm:text-4xl">Available worldwide.</h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-steel">
            Service records and reminders work in any country. Provider matching through Vox Meridian is
            currently strongest in the marketplace’s 10 active markets; outside those markets, visit requests
            go to founder review first.
          </p>
          <div className="mt-8">
            <Link to="/account?next=/dashboard/services" data-testid="service-manager-bottom-cta">
              <VoltButton>Register for the free pilot <ArrowUpRight size={16} weight="bold" /></VoltButton>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceManagerLanding;
